import { useEffect, useRef } from "react";

/**
 * Geolop Asistanı ORB — 2D canvas üzerinde 3D görünümlü dönen partikül küresi.
 * Durumlar: idle (marka kırmızısı, sakin dönüş) · thinking (bordo, nabız) ·
 * speaking (açık kırmızı, genlik dalgası) · listening (gül, geniş nefes).
 * Renkler durumlar arası yumuşak geçer (lerp).
 */
export type OrbState = "idle" | "thinking" | "speaking" | "listening";

const STATE_COLOR: Record<OrbState, [number, number, number]> = {
  idle: [232, 62, 60],       // marka kırmızısı #e83e3c
  thinking: [145, 29, 28],   // koyu bordo (derin düşünce)
  speaking: [236, 100, 97],  // açık kırmızı (canlı konuşma)
  listening: [241, 142, 139], // gül — "sana açığım" hissi
};

export function AssistantOrb({ state, size = 300 }: { state: OrbState; size?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef<OrbState>(state);
  stateRef.current = state;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);

    // Küre yüzeyinde partiküller (fibonacci sphere — dengeli dağılım)
    const N = 520;
    const pts: { x: number; y: number; z: number; s: number; tw: number }[] = [];
    const golden = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < N; i++) {
      const y = 1 - (i / (N - 1)) * 2;
      const r = Math.sqrt(1 - y * y);
      const th = golden * i;
      pts.push({ x: Math.cos(th) * r, y, z: Math.sin(th) * r, s: 0.6 + Math.random() * 1.6, tw: Math.random() * Math.PI * 2 });
    }
    // İç halkalar (Geolop Asistanı hissi)
    const rings = [0.55, 0.72, 0.88];

    let raf = 0;
    let t = 0;
    let color: [number, number, number] = [...STATE_COLOR.idle];
    const lerp = (a: number, b: number, k: number) => a + (b - a) * k;

    const draw = () => {
      const st = stateRef.current;
      t += st === "thinking" ? 0.028 : st === "speaking" ? 0.022 : 0.012;
      const target = STATE_COLOR[st];
      color = [lerp(color[0], target[0], 0.05), lerp(color[1], target[1], 0.05), lerp(color[2], target[2], 0.05)];
      const [cr, cg, cb] = color.map(Math.round);

      const cx = size / 2, cy = size / 2;
      const baseR = size * 0.34;
      // Durum bazlı yarıçap modülasyonu
      const pulse = st === "thinking" ? Math.sin(t * 3) * 0.05
        : st === "speaking" ? (Math.sin(t * 7) * 0.04 + Math.sin(t * 13) * 0.03)
        : st === "listening" ? Math.sin(t * 1.6) * 0.07
        : Math.sin(t * 1.2) * 0.015;
      const R = baseR * (1 + pulse);

      ctx.clearRect(0, 0, size, size);

      // Dış parlama
      const glow = ctx.createRadialGradient(cx, cy, R * 0.2, cx, cy, R * 1.7);
      glow.addColorStop(0, `rgba(${cr},${cg},${cb},0.16)`);
      glow.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, size, size);

      const rotY = t * 0.6, rotX = Math.sin(t * 0.3) * 0.35 + 0.25;
      const cosY = Math.cos(rotY), sinY = Math.sin(rotY);
      const cosX = Math.cos(rotX), sinX = Math.sin(rotX);

      // Halkalar
      for (let ri = 0; ri < rings.length; ri++) {
        ctx.beginPath();
        ctx.strokeStyle = `rgba(${cr},${cg},${cb},${0.12 + ri * 0.05})`;
        ctx.lineWidth = 1;
        const rr = R * rings[ri];
        const tilt = 0.5 + ri * 0.4 + t * (ri % 2 ? 0.2 : -0.15);
        for (let a = 0; a <= Math.PI * 2 + 0.05; a += 0.08) {
          const x0 = Math.cos(a) * rr, z0 = Math.sin(a) * rr;
          const y0 = z0 * Math.sin(tilt) * 0.5;
          const z1 = z0 * Math.cos(tilt);
          const px = cx + x0 * cosY + z1 * sinY;
          const py = cy + y0 * cosX - (z1 * cosY - x0 * sinY) * sinX * 0.3;
          if (a === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        }
        ctx.stroke();
      }

      // Partiküller (z-sıralı — arkadakiler soluk)
      const projected = pts.map((p) => {
        const x1 = p.x * cosY + p.z * sinY;
        const z1 = p.z * cosY - p.x * sinY;
        const y1 = p.y * cosX - z1 * sinX;
        const z2 = z1 * cosX + p.y * sinX;
        const jitter = st === "speaking" ? Math.sin(t * 9 + p.tw) * 0.05 : st === "thinking" ? Math.sin(t * 5 + p.tw) * 0.03 : 0;
        const rr = R * (1 + jitter);
        return { px: cx + x1 * rr, py: cy + y1 * rr, z: z2, s: p.s, tw: p.tw };
      }).sort((a, b) => a.z - b.z);

      for (const p of projected) {
        const depth = (p.z + 1) / 2; // 0 arka, 1 ön
        const alpha = 0.15 + depth * 0.75 * (0.7 + 0.3 * Math.sin(t * 2 + p.tw));
        ctx.beginPath();
        ctx.fillStyle = `rgba(${cr},${cg},${cb},${alpha.toFixed(3)})`;
        ctx.arc(p.px, p.py, p.s * (0.5 + depth * 0.9), 0, Math.PI * 2);
        ctx.fill();
      }

      // Çekirdek
      const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 0.35);
      core.addColorStop(0, `rgba(255,255,255,${st === "speaking" ? 0.5 + Math.abs(Math.sin(t * 8)) * 0.3 : 0.35})`);
      core.addColorStop(0.4, `rgba(${cr},${cg},${cb},0.25)`);
      core.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = core;
      ctx.beginPath();
      ctx.arc(cx, cy, R * 0.4, 0, Math.PI * 2);
      ctx.fill();

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [size]);

  return <canvas ref={canvasRef} style={{ width: size, height: size }} />;
}
